import { useCallback, useEffect, useState } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import {
  isQuickFillSessionUndoSnapshotCompatible,
} from '../../../shared/stores/useQuickFillStore';
import { perf } from '../../../shared/lib/perfLogger';
import {
  normalizeQuickFillLabel,
} from '../lib/quickFillLabelNormalization';
import {
  buildQuickFillFocusConsumptionKey,
  resolveQuickFillFocusMatchIndices,
  resolveQuickFillFocusWaitReasons,
  resolveQuickFillRepairRowIndices,
  resolveQuickFillSuggestedCellUpdates,
} from '../lib/quickFillFocusResolution';
import { buildQuickFillFocusPreset } from '../lib/quickFillValidationFocus';
import {
  buildFailedCellErrors,
  buildParameterToLabelMap,
  buildRollbackPropertyMappings,
  cloneQuickFillRows,
} from '../lib/quickFillPageModel';
import type { QuickFillPageExperienceController } from './useQuickFillPageExperienceController';

export function useQuickFillPageValidationController(
  controller: QuickFillPageExperienceController,
  onGoBack: () => void
) {
  const {
    hiddenValidationFocusLabels, validationFocus, safeProperties, extraProperties,
    updatePropertyChecked, updateExtraPropertyChecked, quickFillData,
    effectiveRowMode, validationFocusColumnsReadyInData, validationFocusDatasetModeReady,
    quickFillDataRowModes, initialization, consumedValidationFocusRequestIdRef,
    lastValidationFocusWaitStateRef, operations, cellSelection, navigation,
    tour, contextMenu, setContextMenu, submit,
    propertyPanelOpen, setPropertyPanelOpen, isTableLoading, quickFillSessionUndoTarget,
    quickFillSessionUndoTargetIndex, safeSessionUndoSnapshots, restoreSessionUndoSnapshot,
    clearSessionUndoSnapshots, setIsUndoingQuickFillSessions, snackbar,
    parameterAddition, connectorUndoAffordance,
  } = controller;
  // The validation-focus hook owns the validation-navigation store boundary.
  const { clearIssueNavigationContext } = validationFocus;
  const [validationRevealVersion, setValidationRevealVersion] = useState(0);
  const setFindReplaceOpen = tour.setFindReplaceOpen;

  useEffect(() => {
    if (hiddenValidationFocusLabels.length === 0) return;
    perf.log('QF-Handoff', 'Revealing hidden columns for validation focus', {
      requestId: validationFocus.activeValidationQuickFillFocus?.requestId || null,
      labels: hiddenValidationFocusLabels,
    });
    let revealed = false;
    hiddenValidationFocusLabels.forEach((label) => {
      const normalized = normalizeQuickFillLabel(label);
      const existing = safeProperties.find(
        (property) => normalizeQuickFillLabel(property.label) === normalized
      );
      if (existing && !existing.checked) {
        updatePropertyChecked(existing.id, true);
        revealed = true;
        return;
      }
      const extra = (Array.isArray(extraProperties) ? extraProperties : []).find(
        (property) => normalizeQuickFillLabel(property.label) === normalized
      );
      if (extra && !extra.checked) {
        updateExtraPropertyChecked(extra.id, true);
        revealed = true;
      }
    });
    if (revealed) setValidationRevealVersion((current) => current + 1);
  }, [
    extraProperties, hiddenValidationFocusLabels, safeProperties,
    updateExtraPropertyChecked, updatePropertyChecked,
    validationFocus.activeValidationQuickFillFocus?.requestId,
  ]);

  useEffect(() => {
    const currentFocus = controller.activeValidationQuickFillFocusForColumns;
    if (!currentFocus?.requestId) return;
    const focusConsumptionKey = buildQuickFillFocusConsumptionKey(currentFocus);
    const expectedRowMode = controller.shouldUseFamilyTypeFallbackForValidation
      ? 'familyType'
      : currentFocus.preferredRowMode ?? 'familyType';
    const waitReasons = resolveQuickFillFocusWaitReasons({
      effectiveRowMode,
      expectedRowMode,
      hiddenFocusLabelCount: hiddenValidationFocusLabels.length,
      isTableLoading,
      columnsReadyInData: validationFocusColumnsReadyInData,
      datasetModeReady: validationFocusDatasetModeReady,
      dataRowModes: quickFillDataRowModes,
      rowCount: quickFillData.length,
      validationScopeResolvedEmpty: initialization.validationScopeResolvedEmpty ?? false,
    });
    if (waitReasons.length > 0) {
      const waitState = `${currentFocus.requestId}:${waitReasons.join('|')}:${quickFillData.length}`;
      if (lastValidationFocusWaitStateRef.current !== waitState) {
        lastValidationFocusWaitStateRef.current = waitState;
        perf.log('QF-Handoff', 'Waiting to resolve validation focus', {
          requestId: currentFocus.requestId,
          waitReasons,
          rowMode: effectiveRowMode,
          expectedRowMode,
          quickFillRows: quickFillData.length,
          quickFillDataRowModes,
          labels: currentFocus.labels,
          elementIds: currentFocus.elementIds,
          familyTypeKeys: currentFocus.familyTypeKeys,
          validationScopeResolvedEmpty: initialization.validationScopeResolvedEmpty ?? false,
        });
      }
      return;
    }
    const matchedRowIndices = resolveQuickFillFocusMatchIndices(
      quickFillData,
      currentFocus,
      effectiveRowMode
    );
    perf.log('QF-Handoff', 'Attempted to resolve validation focus', {
      requestId: currentFocus.requestId,
      labels: currentFocus.labels,
      elementIds: currentFocus.elementIds,
      familyTypeKeys: currentFocus.familyTypeKeys,
      rowCandidates: currentFocus.rowCandidates,
      matchedRowIndices,
      sampleRows: quickFillData.slice(0, 8).map((item, index) => ({
        index,
        name: item?.Name || '',
        elementId: (item as any)?.__elementId || '',
        familyTypeKey: (item as any)?.__familyTypeKey || '',
      })),
    });
    if (matchedRowIndices.length === 0) {
      if (consumedValidationFocusRequestIdRef.current === focusConsumptionKey) return;
      operations.replaceSelectedRows([]);
      navigation.setNavigationMarkerRows([]);
      navigation.setNavigationRequest(null);
      controller.setValidationFocusPreset(null);
      consumedValidationFocusRequestIdRef.current = focusConsumptionKey;
      perf.warn('QF-Handoff', 'Validation focus resolved to no rows', {
        requestId: currentFocus.requestId,
        labels: currentFocus.labels,
        elementIds: currentFocus.elementIds,
        familyTypeKeys: currentFocus.familyTypeKeys,
        rowCandidates: currentFocus.rowCandidates,
      });
      validationFocus.setActiveValidationQuickFillFocus(null);
      validationFocus.setValidationScopeInfo(null);
      clearIssueNavigationContext();
      return;
    }
    const repairRowIndices = resolveQuickFillRepairRowIndices(
      quickFillData,
      matchedRowIndices,
      currentFocus
    );
    const matchedRowNames = repairRowIndices.map(
      (index) => quickFillData[index]?.Name || ''
    ).filter(Boolean);
    const suggestedUpdates = resolveQuickFillSuggestedCellUpdates(
      quickFillData,
      repairRowIndices,
      currentFocus
    );
    if (
      consumedValidationFocusRequestIdRef.current === focusConsumptionKey &&
      suggestedUpdates.length === 0
    ) return;
    controller.setValidationFocusPreset(
      buildQuickFillFocusPreset(currentFocus, matchedRowNames, quickFillData)
    );
    operations.replaceSelectedRows(repairRowIndices);
    if (suggestedUpdates.length > 0) {
      operations.stageCellUpdates(suggestedUpdates.map((update) => ({
        rowIndex: update.rowIndex,
        field: update.label as keyof QuickFillItem,
        value: update.value,
      })), 'Validation END-TYPE slot swap');
      controller.cellSelection.selectCells?.(suggestedUpdates.map((update) => ({
        rowIndex: update.rowIndex,
        columnLabel: update.label,
      })));
    } else {
      const focusColumn = currentFocus.primaryLabel ?? currentFocus.labels[0] ?? null;
      if (focusColumn) controller.cellSelection.selectCells?.(
        repairRowIndices.map((rowIndex) => ({ rowIndex, columnLabel: focusColumn }))
      );
    }
    navigation.setNavigationMarkerRows([]);
    navigation.setNavigationRequest({ rowIndex: repairRowIndices[0], requestId: Date.now() });
    perf.log('QF-Handoff', 'Resolved validation focus rows', {
      requestId: currentFocus.requestId,
      matchedRowIndices,
      repairRowIndices,
      matchedRowNames,
      autofillLabels: currentFocus.autofillLabels,
      autofillMode: currentFocus.autofillMode,
      suggestedCellUpdateCount: suggestedUpdates.length,
    });
    consumedValidationFocusRequestIdRef.current = focusConsumptionKey;
    lastValidationFocusWaitStateRef.current = null;
    clearIssueNavigationContext();
  }, [
    clearIssueNavigationContext, consumedValidationFocusRequestIdRef, controller,
    effectiveRowMode, hiddenValidationFocusLabels.length, initialization,
    isTableLoading, lastValidationFocusWaitStateRef, navigation, operations,
    quickFillData, quickFillDataRowModes, validationFocus,
    validationFocusColumnsReadyInData, validationFocusDatasetModeReady,
    validationRevealVersion,
  ]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (contextMenu.open) {
        setContextMenu({ open: false, position: { x: 0, y: 0 }, rowIndex: null });
      } else if (tour.csvImportOpen) {
        tour.setCSVImportOpen(false);
      } else if (submit.saveResultDialog.open) {
        submit.setSaveResultDialog({ open: false, result: null });
      } else if (tour.findReplaceOpen) {
        tour.setFindReplaceOpen(false);
        controller.clearFindReplaceFocus();
      } else if (tour.autoFillDialog.open) {
        tour.handleCancelAutoFill();
      } else if (tour.parameterChangeDialog.open) {
        tour.handleCancelParameterChange();
      } else if (propertyPanelOpen) {
        setPropertyPanelOpen(false);
      } else if (cellSelection.selectedCells.length > 0) {
        cellSelection.clearSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    cellSelection, contextMenu.open, controller, propertyPanelOpen, setContextMenu,
    setPropertyPanelOpen, submit, tour,
  ]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'h') {
        event.preventDefault();
        setFindReplaceOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setFindReplaceOpen]);

  const guardedGoBack = useCallback(() => {
    if (isTableLoading || submit.isContinuing || operations.isSyncing) {
      submit.setPendingNavigation(() => onGoBack);
    } else {
      onGoBack();
    }
  }, [isTableLoading, onGoBack, operations.isSyncing, submit]);
  const handleUndoParameterSetup = useCallback(async () => {
    if (await parameterAddition.undoLastConnectorParameterAddition()) onGoBack();
  }, [onGoBack, parameterAddition]);
  const handleUndoQuickFillSessions = useCallback(async () => {
    const snapshot = quickFillSessionUndoTarget;
    if (!snapshot) {
      snackbar.showWarning('No Quick Fill undo history is available yet.');
      return;
    }
    if (!isQuickFillSessionUndoSnapshotCompatible(snapshot, quickFillData)) {
      snackbar.showWarning(
        'Quick Fill undo is blocked because the current rows no longer match the saved snapshot.'
      );
      return;
    }
    const consumedIds = (quickFillSessionUndoTargetIndex >= 0
      ? safeSessionUndoSnapshots.slice(0, quickFillSessionUndoTargetIndex + 1)
      : [snapshot]
    ).filter(
      (candidate) => isQuickFillSessionUndoSnapshotCompatible(candidate, quickFillData)
    ).map((candidate) => candidate.id);
    const currentData = cloneQuickFillRows(quickFillData);
    const restoredData = cloneQuickFillRows(snapshot.quickFillData);
    const rollbackMappings = buildRollbackPropertyMappings(
      snapshot,
      safeProperties,
      Array.isArray(extraProperties) ? extraProperties : []
    );
    setIsUndoingQuickFillSessions(true);
    try {
      const result = await operations.saveRollbackToRevit(
        currentData,
        restoredData,
        rollbackMappings
      );
      submit.setFailedCellErrors(buildFailedCellErrors(
        result,
        quickFillData,
        buildParameterToLabelMap(rollbackMappings)
      ));
      if (result.failureCount > 0) {
        submit.setSaveResultDialog({
          open: true,
          result,
          warning: 'Quick Fill undo could not roll back every Revit value. Undo history was kept so you can retry.',
        });
        snackbar.showWarning(
          `Undo rolled back ${result.successCount} values, but ${result.failureCount} failed in Revit.`
        );
        return;
      }
      if (!restoreSessionUndoSnapshot(snapshot.id)) {
        snackbar.showWarning(
          'Quick Fill undo saved to Revit, but the local rows changed before the table could be restored.'
        );
        return;
      }
      clearSessionUndoSnapshots(consumedIds);
      submit.setSaveResultDialog({ open: false, result: null });
      snackbar.showSuccess('Undid last Quick Fill session.');
    } catch (error) {
      snackbar.showWarning(
        `Quick Fill undo failed: ${error instanceof Error ? error.message : 'Unknown rollback error'}`
      );
    } finally {
      setIsUndoingQuickFillSessions(false);
    }
  }, [
    clearSessionUndoSnapshots, extraProperties, operations, quickFillData,
    quickFillSessionUndoTarget, quickFillSessionUndoTargetIndex,
    restoreSessionUndoSnapshot, safeProperties, safeSessionUndoSnapshots,
    setIsUndoingQuickFillSessions, snackbar, submit,
  ]);
  const hasCompatibleUndo = controller.compatibleSessionUndoSnapshots.length > 0;
  const quickFillSessionUndoDisabled = controller.isUndoingQuickFillSessions ||
    submit.isContinuing || operations.isSyncing || isTableLoading || !hasCompatibleUndo;
  const quickFillUndoTooltip = safeSessionUndoSnapshots.length === 0
    ? 'No Quick Fill undo history yet'
    : hasCompatibleUndo
      ? `Undo last Quick Fill session${controller.compatibleSessionUndoSnapshots.length > 1 ? ` (${controller.compatibleSessionUndoSnapshots.length - 1} earlier in history)` : ''}`
      : 'Quick Fill undo history does not match the current rows';
  const connectorUndoTooltip = parameterAddition.lastConnectorUndoPlan?.status === 'undo-failed'
    ? parameterAddition.lastConnectorUndoPlan.error || 'Connector setup revert failed. Retry from here.'
    : 'Revert connector parameter setup and return to Connectors';

  return {
    ...controller,
    guardedGoBack,
    handleUndoParameterSetup,
    handleUndoQuickFillSessions,
    hasUndoableParameterSetup: connectorUndoAffordance.canRevert,
    quickFillSessionUndoDisabled,
    quickFillUndoTooltip,
    connectorUndoTooltip,
  };
}

export type QuickFillPageValidationController = ReturnType<
  typeof useQuickFillPageValidationController
>;
